import React, { useRef } from "react";
import { Trophy } from "lucide-react";
import { formatMoney2, formatNumber } from "../lib/api";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";

export function RankingLaboratorios({ rows, title, selected, onPick }) {
  const cache = useRef(null);
  if (!selected) cache.current = rows;
  const data = ((selected && cache.current) || rows || []).filter((r) => r.label)
    .map((r) => (r.label === "nan" || r.label === "Sin asignar" ? { ...r, label: "Sin laboratorio" } : r));
  const total = data.reduce((t, r) => t + (r.valor_pmc || 0), 0);
  const withPct = data.map((r) => ({ ...r, pct: total ? (100 * (r.valor_pmc || 0)) / total : 0 }));
  const sort = useSortable(withPct, "valor_pmc", "desc");
  const max = Math.max(...withPct.map((r) => r.valor_pmc || 0), 1);
  if (!data.length) return null;
  return (
    <div className="card p-6 mt-6" data-testid="ranking-labs-panel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Trophy size={11} /> Ranking</div>
          <h3 className="font-display text-xl font-bold">Ranking por laboratorio</h3>
          <p className="text-xs text-slate-500 mt-1">Laboratorios con más dinero inmovilizado en stock parado. Pulsa uno para filtrar el listado.</p>
        </div>
        <ExportButtons filename="stock-parado-ranking-laboratorios" title={`${title} · Ranking por laboratorio`} sheetName="Laboratorios"
                       columns={[{ header: "Laboratorio", key: "label" }, { header: "Productos", key: "productos", align: "right" },
                                 { header: "Stock", key: "stock", align: "right" },
                                 { header: "Valor PMC (€)", key: "valor_pmc", format: (v) => (v || 0).toFixed(2), align: "right" },
                                 { header: "Valor PVP (€)", key: "valor_pvp", format: (v) => (v || 0).toFixed(2), align: "right" },
                                 { header: "% del total", key: "pct", format: (v) => (v || 0).toFixed(1), align: "right" }]}
                       rows={sort.sortedRows} testIdPrefix="export-ranking-labs" />
      </div>
      <div className="overflow-x-auto max-h-[32rem]">
        <table className="dense" data-testid="ranking-labs-table">
          <thead>
            <tr>
              <th>#</th>
              <th><SortHeader label="Laboratorio" k="label" sort={sort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Productos" k="productos" sort={sort} num /></th>
              <th className="num"><SortHeader label="Stock" k="stock" sort={sort} num /></th>
              <th className="num"><SortHeader label="Valor PMC" k="valor_pmc" sort={sort} num /></th>
              <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={sort} num /></th>
              <th style={{ minWidth: 160 }}><SortHeader label="% del total" k="pct" sort={sort} num /></th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.map((r, i) => (
              <tr key={r.label} onClick={() => r.label !== "Sin laboratorio" && onPick(selected === r.label ? "" : r.label)} data-testid={`ranking-lab-row-${r.label}`}
                  className={"cursor-pointer " + (selected === r.label ? "bg-slate-100 font-semibold" : "")}>
                <td className="font-mono text-xs text-slate-500">{i + 1}</td>
                <td className="text-xs font-semibold">{r.label}</td>
                <td className="num">{formatNumber(r.productos)}</td>
                <td className="num">{formatNumber(r.stock)}</td>
                <td className="num text-amber-700 font-semibold">{formatMoney2(r.valor_pmc)}</td>
                <td className="num">{formatMoney2(r.valor_pvp)}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-red-500" style={{ width: `${(100 * (r.valor_pmc || 0)) / max}%` }} /></div>
                    <span className="text-xs font-mono w-12 text-right">{r.pct.toFixed(1).replace(".", ",")} %</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
